<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="isFullScreen"
      max-width="850"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          class="pl-1"
          flat
          height="80"
          max-height="80"
        >
          <div class="mr-3">
            <v-avatar color="indigo">
              <v-icon color="indigo lighten-5">
                email
              </v-icon>
            </v-avatar>
          </div>

          <v-toolbar-title class="pl-1">
            <div class="body-3">
              Message Templates
            </div>

            <div class="text-caption">
              Please select message template
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            class="ml-0"
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-3">
          <v-container
            class="pa-0"
            fluid
          >
            <v-row>
              <template v-for="template in templates">
                <v-col
                  :key="`template-${template.Id}`"
                  cols="auto"
                >
                  <v-lazy
                    :options="{threshold: .5}"
                    transition="fade-transition"
                  >
                    <v-hover v-slot="{hover}">
                      <v-card
                        max-width="360"
                        width="360"
                      >
                        <v-img
                          v-if="template.Image"
                          :src="template.Image"
                          height="285"
                          max-height="285"
                          position="top"
                        >
                          <template #placeholder>
                            <v-row
                              align="center"
                              class="fill-height"
                              justify="center"
                            >
                              <v-progress-circular
                                :width="2"
                                color="grey lighten-2"
                                indeterminate
                              />
                            </v-row>
                          </template>

                          <v-expand-transition>
                            <div
                              v-if="hover"
                              class="d-flex transition-fast-in-fast-out text-h2 white--text v-card--reveal"
                            >
                              <div class="text-center">
                                <v-btn
                                  :disabled="template.loadingEdit"
                                  :loading="template.loadingEdit"
                                  class=""
                                  color="green"
                                  dark
                                  depressed
                                  style="min-width: 225px"
                                  @click.stop="onTemplateNewClick(template)"
                                >
                                  <v-icon left>
                                    mdi-application-edit-outline
                                  </v-icon>

                                  <div>New Message</div>
                                </v-btn>

                                <v-btn
                                  v-if="template.Id > 0"
                                  :disabled="template.loadingEdit"
                                  :loading="template.loadingEdit"
                                  class=""
                                  color="primary"
                                  dark
                                  depressed
                                  style="min-width: 225px"
                                  @click.stop="onTemplateEditClick(template)"
                                >
                                  <v-icon left>
                                    mdi-application-cog-outline
                                  </v-icon>

                                  <div>Edit Template</div>
                                </v-btn>

                                <v-btn
                                  v-if="!!template.Image"
                                  :disabled="template.loadingPreview"
                                  :loading="template.loadingPreview"
                                  class=""
                                  color="blue-grey"
                                  dark
                                  depressed
                                  style="min-width: 225px"
                                  @click.stop="onTemplatePreviewClick(template)"
                                >
                                  <v-icon left>
                                    mdi-file-find
                                  </v-icon>

                                  <div>Preview Template</div>
                                </v-btn>

                                <v-btn
                                  v-if="template.Id > 0"
                                  :disabled="template.loadingDelete"
                                  :loading="template.loadingDelete"
                                  color="red"
                                  depressed
                                  style="min-width: 225px"
                                  text
                                  @click.stop="onTemplateDeleteClick(template)"
                                >
                                  <v-icon left>
                                    mdi-close-circle-outline
                                  </v-icon>

                                  <div>Delete Template</div>
                                </v-btn>
                              </div>
                            </div>
                          </v-expand-transition>
                        </v-img>

                        <v-card
                          v-else
                          class="text-center rounded-b-0 grey"
                          flat
                          height="285"
                        >
                          <div class="d-flex fill-height align-center justify-center">
                            <v-icon
                              size="64"
                              style="opacity: 0.35;"
                            >
                              mdi-image-off-outline
                            </v-icon>
                          </div>

                          <v-expand-transition>
                            <div
                              v-if="hover"
                              class="d-flex transition-fast-in-fast-out text-h2 white--text v-card--reveal"
                            >
                              <div class="text-center">
                                <v-btn
                                  :disabled="template.loadingEdit"
                                  :loading="template.loadingEdit"
                                  class=""
                                  color="green"
                                  dark
                                  depressed
                                  style="min-width: 225px"
                                  @click.stop="onTemplateNewClick(template)"
                                >
                                  <v-icon left>
                                    mdi-application-edit-outline
                                  </v-icon>

                                  <div>New Message</div>
                                </v-btn>
                              </div>
                            </div>
                          </v-expand-transition>
                        </v-card>

                        <v-divider />

                        <v-card-title>{{ (template.Name || 'Name...') | truncate(31) }}</v-card-title>
                        <v-card-subtitle>{{ (template.Subject || 'Subject...') | truncate(45) }}</v-card-subtitle>
                      </v-card>
                    </v-hover>
                  </v-lazy>
                </v-col>
              </template>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3">
          <v-pagination
            v-model="page"
            :length="totalPages"
            :total-visible="$vuetify.breakpoint.xsOnly ? 0 : 7"
          />

          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm-dialog
      v-if="selectedTemplateDelete"
      :html-content="$t('Message.Template.ConfirmDeleteDialog.Content', {title: selectedTemplateDelete.Subject})"
      :html-title="$t('Message.Template.ConfirmDeleteDialog.Title')"
      :info-bar="true"
      :info-bar-body="$t('Message.Template.ConfirmDeleteDialog.Warning')"
      :visible.sync="confirmDialogDeleteVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteTemplate"
    />

    <message-details-dialog
      :message="selectedMessagePreview"
      :visible.sync="messagePreviewDialogVisible"
    />
  </div>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import DialogVisible from '@/mixins/dialogVisible'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import MessageDetailsDialog from '@/components/messages/MessageDetailsDialog.vue'
import MessageModel from '@/api/Models/messages/MessageModel'

export default {
  name      : 'MessageTemplatesDialog',
  components: {
    MessageDetailsDialog,
    ConfirmDialog
  },
  mixins: [Auth, AppData, DialogVisible],
  props : {},
  data() {
    return {
      isFullScreen               : true,
      page                       : 1,
      perPage                    : 9,
      viewData                   : null,
      confirmDialogDeleteVisible : false,
      selectedTemplateDelete     : null,
      selectedMessagePreview     : null,
      messagePreviewDialogVisible: false
    }
  },
  computed: {
    templates() {
      const retVal = this.viewData?.items || []
      return [...retVal]
    },
    templatesCount() {
      return this.viewData?.count || 0
    },
    totalPages() {
      return Math.ceil(this.templatesCount / this.perPage)
    }
  },
  watch: {
    page() {
      this.getData()
    },

    isVisible(newVal) {
      if (newVal) {
        this.getData()
      } else {
        this.viewData = null
      }
    }
  },
  created() {
    this.$bus.$on(window.SocketCommand.Message.Templates, this.onGetDataResult)
    this.$bus.$on(window.SocketCommand.Message.GetTemplate, this.onGetTemplateResult)
    this.$bus.$on(window.SocketCommand.Message.DeleteTemplate, this.onTemplateDeleteResult)
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Message.Templates, this.onGetDataResult)
    this.$bus.$off(window.SocketCommand.Message.GetTemplate, this.onGetTemplateResult)
    this.$bus.$off(window.SocketCommand.Message.DeleteTemplate, this.onTemplateDeleteResult)
  },
  methods: {
    getData() {
      window.callAS(window.SocketCommand.Message.Templates, {
        page      : this.page,
        page_limit: this.perPage
      })
    },
    onGetDataResult(data) {
      this.viewData = data || null
      if (Array.isArray(this.viewData?.items)) {
        this.viewData.items.unshift(
          {
            Id     : -1,
            Name   : 'Blank Template',
            Subject: 'Start editing with a blank template',
            Image  : ''
          }
        )
      }
    },

    onTemplatePreviewClick(template) {
      this.$set(template, 'loadingPreview', true)
      window.callAS(window.SocketCommand.Message.GetTemplate, {
        Id     : template.Id,
        Preview: true
      })
    },

    onTemplateNewClick(template) {
      this.$set(template, 'loadingEdit', true)
      if (template.Id > 0) {
        window.callAS(window.SocketCommand.Message.GetTemplate, {
          Id : template.Id,
          New: true
        })
      } else {
        this.$emit('template:selected', null)
        this.onCancelButtonClick()
      }
    },
    onTemplateEditClick(template) {
      this.$set(template, 'loadingEdit', true)
      if (template.Id > 0) {
        window.callAS(window.SocketCommand.Message.GetTemplate, {
          Id  : template.Id,
          Edit: true
        })
      } else {
        this.$emit('template:selected', null)
        this.onCancelButtonClick()
      }
    },
    onGetTemplateResult(data, payload) {
      const template = this.templates.find(template => template.Id === payload.Id)
      if (template) {
        this.$set(template, 'loadingPreview', false)
        this.$set(template, 'loadingEdit', false)
      }

      const message = {
        Id           : data.data.Id,
        Body         : data.data.Body,
        BodyText     : data.data.BodyText,
        Source       : data.data.Source,
        TemplateId   : data.data.TemplateId,
        TemplateAlias: data.data.TemplateAlias,
        Type         : data.data.Type
      }

      if (data?.status === 'success' && data?.data) {
        if (payload.Preview) {
          this.selectedMessagePreview = new MessageModel(message)
          this.messagePreviewDialogVisible = true
        } else {
          if (payload.New) message.Id = null

          this.$emit('template:selected', message)
          this.onCancelButtonClick()
        }
      }
    },

    onTemplateDeleteClick(template) {
      this.selectedTemplateDelete = template
      this.confirmDialogDeleteVisible = true
    },
    onConfirmDeleteTemplate() {
      this.confirmDialogDeleteVisible = false
      this.$set(this.selectedTemplateDelete, 'loadingDelete', true)
      window.callAS(window.SocketCommand.Message.DeleteTemplate, {Id: this.selectedTemplateDelete.Id})
    },
    onTemplateDeleteResult(data, payload) {
      if (data?.status === 'success') {
        this.getData()

        if (data?.status === 'success') {
          if (data && data.status === 'success') {
            this.$bus.$emit('app-show-notification', {
              body: this.$t('Message.Template.DeleteSuccess'),
              type: 'success',
              icon: 'info'
            })
          } else {
            this.$bus.$emit('app-show-notification', {
              body: this.$t('Common.Error.Generic'),
              type: 'error',
              icon: 'warning'
            })
          }
        }
      }
    },

    onCancelButtonClick() {
      this.closeDialog()
    }
  }
}
</script>

<style scoped>
:deep(.v-card--reveal .v-btn__content) {
  justify-content: flex-start;
}

:deep(.v-card--reveal .v-btn__content div) {
  text-align: center;
  width: 100%;
}

.v-card--reveal {
  position: absolute;
  align-items: center;
  justify-content: center;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(30, 30, 30, 0.90);
}
</style>
